/**
 * Chats (`/conversations`) — één op één met de oude pagina.
 *
 * Alle negen zijpanelen, de modelkaarten met foto, de uitklapbalk met
 * verzonden mass messages en de tabel met alle chats. Inhoud en indeling zijn
 * die van de bundle; alleen de opmaak is nieuw.
 *
 * Endpoints en pollingintervallen zijn gemeten aan de draaiende oude pagina
 * (scratchpad/traffic.py) en hier gelijkgetrokken:
 *
 *   conversations                                 2,5 s
 *   dashboard/activity            (Live Feed)     2,5 s
 *   (26-10: recent-unlocks staat niet meer op deze pagina)
 *   conversations?vip=true (paneel VIP)        15 s
 *   models/personas                               5 s
 *   conversations (queue_candidates)              5 s
 *   conversations/panels/private                 12 s
 *
 * De vier wachtrij-panelen komen uit ÉÉN query en worden client-side verdeeld
 * (zie `lib/panels.ts`) — precies zoals de backend het beschrijft.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Avatar, ErrorNote, Loading, Panel, Plat, Seg, Table, Tag, Wacht } from '../components/ui'
import { useApi, usePoll } from '../lib/useApi'
import { useLiveFeed } from '../lib/useLiveFeed'
import { KlokTeller } from '../components/KlokTeller'
import { MobielInboxChips } from '../components/MobielInboxChips'
import { useMobiel } from '../lib/mobiel'
import { FEED_KNOP_DAGEN } from '../lib/liveFeed'
import { useActions, mut } from '../lib/actions'
import { api } from '../lib/api'
import { alleModellen } from '../lib/spendAlleModellen'
import { useAiStand } from '../lib/aiStatus'
import { isBeheer, magAiSchakelen, magBetaaldeMassDm, magPriveBeheer, useMij } from '../lib/rechten'
import { magMassDm } from '../lib/massaRechten'
import { ergsteUrgentie } from '../lib/urgentie'
import { dtFull, eur, num, waited } from '../lib/format'
import { alreadyOpened, bucketPanels, isGemarkeerd, nieuweVolgers, vipLijst } from '../lib/panels'
import type { QueueRow } from '../lib/panels'
import { NieuweAbonneeTag } from '../components/NieuweAbonneeTag'
import {
  LiveFeedPanel, MassSentBar, PrivatePanel, SpendersPanel,
  VipPanel, TimewastersPanel, MyChatsPanel, NewFollowersPanel, WaitingPanel, OnlinePanel, FilterBalk,
} from '../components/ChatPanels'
import type { ActivityRow, MassRow, PrivateRow } from '../components/ChatPanels'
import type { Persona } from './Models'
import { MassDmSendDialog } from '../components/MassDmSendDialog'
import { MijnGeplandeMassDm } from '../components/MijnGeplandeMassDm'
import { ClaimsLosmaken } from '../components/ClaimsLosmaken'
import { WegklikBevestig, WeggekliktLijst } from '../components/Weggeklikt'
import { PriveBeheerDialog } from '../components/PriveBeheerDialog'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { modelTegels, openPerSlug, tegelsOpOpen } from '../lib/modelTegels'
import { filterQs, leesFilter, modelKies, platformKies, schrijfFilter, tegelKlik } from '../lib/chatsFilter'
import type { ChatsFilter } from '../lib/chatsFilter'
import {
  aantalVersOnline, actiefFilter, bewaarKeuze, kopTotaal, leesKeuze, ONLINE_VERS_MIN, STANDAARD_KEUZE,
  vipOnlineLijst, volgerLijst,
} from '../lib/volgersFilter'
import type { VolgerKeuze } from '../lib/volgersFilter'
import { useUiPref } from '../lib/inklappen'
import ChatsEasy from './ChatsEasy'
import { F2fLoginMelding } from '../components/F2fLoginMelding'

interface Convo extends QueueRow {
  id: number
  platform: string
  model_slug: string
  avatar_url: string | null
  stage_name: string | null
  model_persona_id: number
  fan_id: string
  fan_username: string | null
  /** De belangrijke notitie; als badge in de rij. */
  fan_custom_notes: string | null
  fan_display_name: string | null
  fan_total_spend: number
  fan_messages: number
  ai_messages: number
  total_messages: number
  current_tag: string | null
  ai_opted_in: boolean
  is_excluded_from_ai: boolean
  dashboard_blocked: boolean
  last_message: string | null
  last_fan_message_at: string | null
  last_read_at: string | null
  last_response_at: string | null
  updated_at: string
  status: string
  claimed_by_name: string | null
}
interface Page<T> { data: T[]; total: number }
interface PrivateResp { conversations: PrivateRow[]; total: number; total_all: number }

const PLATFORMS = [
  { value: 'all', label: 'All' },
  { value: 'fancentro', label: 'FanCentro' },
  { value: 'f2f', label: 'F2F' },
]
/** De oude pagina start op "Unread only"; de derde knop is "Blocked".
 *  20-10-2026 (Jordy): de pagina opent altijd op "All" (zie `useState('all')`). */
const VIEWS = [
  { value: 'unread', label: 'Unread only' },
  { value: 'all', label: 'All' },
  { value: 'blocked', label: 'Blocked' },
]
const SORTS = [
  { value: 'activity', label: 'Last activity' },
  { value: 'spend', label: 'Spend (high → low)' },
]

const COLS = [
  { key: 'sel', label: '' },
  { key: 'id', label: 'ID' },
  { key: 'fan', label: 'Fan' },
  { key: 'spend', label: 'Spend', num: true },
  { key: 'plat', label: 'Platform' },
  { key: 'model', label: 'Model' },
  { key: 'ai', label: 'AI' },
  { key: 'tag', label: 'Tag' },
  { key: 'msgs', label: 'Msgs', num: true },
  { key: 'fanmsg', label: 'Fan', num: true },
  { key: 'aimsg', label: 'AI', num: true },
  { key: 'last', label: 'Last activity' },
]

const TAG_KIND: Record<string, 'ok' | 'warn' | 'bad' | 'acc' | 'mut'> = {
  hot: 'bad', warm: 'warn', vip: 'acc', buyer_intent: 'ok',
  cold: 'mut', low: 'mut',
}

/** Sommige endpoints geven een lijst, andere {data:[…]}; dit leest beide. */
function listOf<T>(v: T[] | { data: T[] } | undefined | null): T[] {
  if (Array.isArray(v)) return v
  return v?.data ?? []
}

/**
 * De Chats-pagina: een Easy- en een Pro-stand (02-10-2026, Jordy).
 *
 * Pro is de pagina zoals hij was, ongewijzigd. Easy is drie kolommen naast
 * elkaar (gesprekken, het open gesprek, de modellen), zoals de multi-chat van
 * FanCentro. Elke gebruiker kiest zelf; de keuze staat per account op de
 * server (`chats.mode`, zonder waarde = Pro). Er wordt altijd maar één van de
 * twee getekend: beide hebben hun eigen pollers.
 */
export default function Chats() {
  const [stand, zetStand, geladen] = useUiPref<string>('chats.mode', 'pro')
  const easy = stand === 'easy'
  // Nog niet bekend welke stand: niets tekenen, anders start Pro al zijn
  // pollers om een tel later voor Easy plaats te maken.
  if (!geladen) return <Loading />
  const wissel = (
    <div className="seg standwissel" role="tablist" aria-label="Chats layout">
      <button type="button" role="tab" aria-selected={easy}
              className={easy ? 'on' : undefined}
              onClick={() => zetStand('easy')}>Easy</button>
      <button type="button" role="tab" aria-selected={!easy}
              className={easy ? undefined : 'on'}
              onClick={() => zetStand('pro')}>Pro</button>
    </div>
  )
  // 06-10-2026 (Jordy): "F2F login needed" boven beide standen.
  return (
    <>
      <F2fLoginMelding />
      {easy ? <ChatsEasy wissel={wissel} /> : <ChatsPro wissel={wissel} />}
    </>
  )
}

function ChatsPro({ wissel }: { wissel: ReactNode }) {
  /**
   * 06-10-2026 (Jordy): het modelfilter. Tegel (model + platform), keuzelijst
   * "All models" en platformfilter delen één filter in de URL
   * (`?model=…&platform=…`); elk paneel stuurt het naar de server. Zie
   * lib/chatsFilter.ts.
   */
  const [zoekParams, zetZoekParams] = useSearchParams()
  const filter = leesFilter(zoekParams)
  const { platform, model } = filter
  const zetFilter = (f: ChatsFilter) =>
    zetZoekParams((huidig) => schrijfFilter(huidig, f), { replace: true })
  const fqs = filterQs(filter)
  const [view, setView] = useState('all')
  const [sort, setSort] = useState('activity')
  // 20-10-2026 (Jordy): "Online now" is een FILTER (los van de sortering), zodat
  // het samengaat met Spend; plus een eigen platform- en modelfilter voor deze lijst.
  const [alleenOnline, setAlleenOnline] = useState(false)
  // 21-10-2026 (Jordy): All chats-filters Timewasters / Maybe timewasters (één tegelijk).
  const [twFilter, setTwFilter] = useState<'' | 'yes' | 'maybe'>('')
  // 21-10-2026 (Jordy): rijen selecteren voor een bulkactie (alleen owner/admin).
  const [selectie, setSelectie] = useState<Set<number>>(() => new Set())
  const [bulkBezig, setBulkBezig] = useState(false)
  const [lijstPlatform, setLijstPlatform] = useState('all')
  const [lijstModel, setLijstModel] = useState('all')
  const [chatter, setChatter] = useState('all')
  const [feedChatter, setFeedChatter] = useState('all')
  const [search, setSearch] = useState('')
  const [massOpen, setMassOpen] = useState(false)
  const [eigenOpen, setEigenOpen] = useState(false)
  // 19-10-2026 (Jordy): owner/admin kan chats losmaken (alle of per chatter).
  const [losmakenOpen, setLosmakenOpen] = useState<false | true | 'alle' | number>(false)
  // 20-10-2026 (Jordy): wegklikken eerst bevestigen; owner/admin kunnen het terugzetten.
  const [wegklik, setWegklik] = useState<number | null>(null)
  const [geslotenOpen, setGeslotenOpen] = useState(false)
  // Een dubbele klik op de × van een nieuwe volger stuurt maar één verzoek.
  const volgerBezig = useRef(new Set<number>())
  // 19-10-2026 (Jordy): op de telefoon staan de filters achter één knop (dicht).
  const mobiel = useMobiel()
  const [mobielFilters, setMobielFilters] = useState(false)
  const size = 50
  const navigate = useNavigate()
  const { run } = useActions()

  const blocked = view === 'blocked'
  const [alleenAi, setAlleenAi] = useState(false)
  const aiStand = useAiStand()
  const mijnRol = useMij()
  // 01-10-2026: "Manage all" bij Private chats opent weer het beheervenster.
  const [priveBeheerOpen, setPriveBeheerOpen] = useState(false)
  /**
   * 17-09-2026 (Jordy: "de filter werkt hier niet"). Het zoekveld filterde
   * alleen de 50 rijen die toevallig geladen waren, dus zoeken op een fan of
   * een woord uit een gesprek leverde "No chats match this filter" op terwijl
   * het gesprek gewoon bestond. De term gaat nu naar de SERVER, die door alle
   * gesprekken zoekt: fan-id, fan- en modelnaam, en de berichttekst.
   *
   * Vertraagd met 350 ms, zodat er niet per toetsaanslag een query loopt.
   */
  const [zoekTraag, setZoekTraag] = useState('')
  useEffect(() => {
    const h = window.setTimeout(() => setZoekTraag(search.trim()), 350)
    return () => window.clearTimeout(h)
  }, [search])

  // 20-10-2026 (Jordy): geen Previous/Next meer, maar één lange lijst die bij het
  // scrollen blok voor blok bijlaadt. Het eerste blok ververst (poll); de volgende
  // blokken worden één keer opgehaald. "Spend (high → low)" sorteert de SERVER
  // (totale spend over alle modellen), anders klopte het alleen binnen één blok.
  const lijstBasis =
    `/api/admin/conversations?limit=${size}` +
    `&dashboard_blocked=${blocked}` +
    (zoekTraag ? `&search=${encodeURIComponent(zoekTraag)}` : '') +
    // De eigen filters van deze lijst gaan voor; anders geldt het filter van de pagina.
    filterQs({
      model: lijstModel !== 'all' ? lijstModel : filter.model,
      platform: lijstPlatform !== 'all' ? lijstPlatform : filter.platform,
    }) +
    (alleenOnline ? '&online_now=true' : '') +
    (twFilter ? `&timewaster=${twFilter}` : '') +
    (sort === 'spend' ? '&sort_by=spend' : '')
  const grid = useApi<Page<Convo>>(`${lijstBasis}&offset=0`, [lijstBasis])
  const [meer, setMeer] = useState<Convo[]>([])
  const [meerBezig, setMeerBezig] = useState(false)
  const [meerKlaar, setMeerKlaar] = useState(false)
  const meerSleutel = useRef(lijstBasis)
  useEffect(() => {
    meerSleutel.current = lijstBasis
    setMeer([]); setMeerKlaar(false); setMeerBezig(false)
    setSelectie(new Set())
  }, [lijstBasis])
  const laadMeer = async () => {
    if (meerBezig || meerKlaar || !grid.data) return
    const sleutel = lijstBasis
    const offset = size + meer.length
    if (offset >= Number(grid.data.total ?? 0) || (grid.data.data ?? []).length < size) { setMeerKlaar(true); return }
    setMeerBezig(true)
    try {
      const r = await api.get<Page<Convo>>(`${sleutel}&offset=${offset}&skip_total=true`)
      if (meerSleutel.current !== sleutel) return
      const nieuw = r?.data ?? []
      setMeer((m) => [...m, ...nieuw])
      if (nieuw.length < size) setMeerKlaar(true)
    } finally {
      if (meerSleutel.current === sleutel) setMeerBezig(false)
    }
  }
  const eindRef = useRef<HTMLDivElement | null>(null)
  const laadMeerRef = useRef(laadMeer)
  laadMeerRef.current = laadMeer
  useEffect(() => {
    const el = eindRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver((e) => { if (e.some((x) => x.isIntersecting)) void laadMeerRef.current() },
                                        { rootMargin: '600px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [grid.data, meer.length])
  const queue = useApi<Page<Convo>>(
    '/api/admin/conversations?limit=350&offset=0&skip_total=true' +
    '&queue_candidates=true&sort_order=desc&panel=unanswered&dashboard_blocked=false' + fqs,
    [fqs],
  )
  const priv = useApi<PrivateResp>(`/api/admin/conversations/panels/private?limit=80${fqs}`, [fqs])
  // 21-10-2026 (Jordy): paneel "Online": wie nu online is (zelfde filter als
  // "Online now" in All chats), binnen het gekozen model/platform of het hele merk.
  const onlineQ = useApi<Page<Convo> & { online_status_available?: boolean }>(
    // 21-10-2026 (Jordy): geen plafond; de server staat tot 5000 rijen toe.
    '/api/admin/conversations?limit=5000&offset=0&skip_total=true&online_now=true' +
    '&sort_order=desc&dashboard_blocked=false' + fqs,
    [fqs],
  )
  // 18-10-2026 (Jordy): de model-cards tellen de open chats van ELK model. Met een
  // gekozen model is `queue` alleen dat model; dan dezelfde query zonder modelfilter.
  const telPad = model
    ? '/api/admin/conversations?limit=350&offset=0&skip_total=true' +
      '&queue_candidates=true&sort_order=desc&panel=unanswered&dashboard_blocked=false' +
      filterQs({ ...filter, model: '' })
    : null
  const telQueue = useApi<Page<Convo>>(telPad, [telPad])
  // De oude pagina haalt personas ZONDER `split` op en maakt één kaart per
  // SLUG uit `conversation_count_by_slug`; vandaar 29 kaarten uit 23 rijen.
  const personas = useApi<Persona[]>('/api/admin/models/personas')
  const chatters = useApi<{ id: number; name: string }[]>('/api/admin/models/chatters')
  // 28-10-2026 (Jordy): eigen filters in de kop van New followers (lib/volgersFilter.ts),
  // per gebruiker in deze browser bewaard. 30-10-2026: alleen nog "Online only" en
  // "Online first"; platform en model volgen weer het filter van de pagina.
  const mijnId = mijnRol.data?.id ?? null
  const [volgerKeuze, zetVolgerKeuzeRuw] = useState<VolgerKeuze>(STANDAARD_KEUZE)
  useEffect(() => { if (mijnId != null) zetVolgerKeuzeRuw(leesKeuze(mijnId)) }, [mijnId])
  const zetVolgerKeuze = (k: Partial<VolgerKeuze>) => {
    const nieuw = { ...volgerKeuze, ...k }
    zetVolgerKeuzeRuw(nieuw)
    if (mijnId != null) bewaarKeuze(mijnId, nieuw)
  }
  const vfqs = filterQs(filter)
  // 20-10-2026 (Jordy): nieuwe volgers, een eigen query met een eigen limiet,
  // zodat ze nooit echte wachtende fans uit de 350 verdringen.
  // 26-10-2026 (Jordy): New followers als eigen blok; per 100 bijladen, het totaal in de kop.
  const [volgerLimiet, setVolgerLimiet] = useState(100)
  const volgersQ = useApi<Page<Convo>>(
    `/api/admin/conversations?limit=${volgerLimiet}&offset=0&skip_total=true` +
    '&nieuwe_volgers=true&sort_order=desc&panel=unanswered&dashboard_blocked=false' + vfqs,
    [vfqs, volgerLimiet],
  )
  // 28-10-2026: de nieuwe volgers die volgens de server online staan (alle, tot de
  // servergrens van 1000); welke daarvan vers genoeg zijn, bepaalt volgerLijst().
  const volgersOnlineQ = useApi<Page<Convo> & { online_status_available?: boolean }>(
    '/api/admin/conversations?limit=1000&offset=0&skip_total=true&nieuwe_volgers=true&online_now=true' +
    '&sort_order=desc&panel=unanswered&dashboard_blocked=false' + vfqs,
    [vfqs],
  )
  // Let op: deze vier endpoints geven een KALE lijst terug, geen {data:[…]}.
  // `listOf` vangt beide vormen af, zodat een panel niet stil leeg blijft.
  //
  // Beheer leest `/dashboard/...`, een chatter `/chatter/...`. Dat is geen
  // eigen bedenksel maar precies wat oud doet: die routes zijn manager-gated,
  // en toen dat er in juni op kwam verloren chatters hun Live Feed. Er staat
  // sindsdien een chatter-veilige variant naast die alleen BERICHTEN teruggeeft
  // (nooit omzet of posting) en alleen voor de modellen waar de chatter aan is
  // toegewezen. Wij hadden die router al staan maar riepen hem nooit aan —
  // vandaar drie lege panelen en drie 403's per schermwissel.
  const feed = isBeheer(mijnRol.data) ? 'dashboard' : 'chatter'
  // 26-09: de Live Feed groeit mee — kop verversen, onderaan ouder laden
  // (zie lib/liveFeed.ts). Eerst verving elke verversing de lijst door de
  // nieuwste 40 en was er geen weg terug.
  const activity = useLiveFeed<ActivityRow>(feed, platform, model)
  // 21-10-2026 (Jordy): het paneel New Subscriptions is nu VIP (nieuwe subscripties
  // staan bij Spenders): handmatig VIP of spend > 0, blijvend, wachtenden bovenaan.
  // 30-10-2026 (Jordy): dezelfde filters in de kop van VIP als bij New followers, met
  // een EIGEN keuze (eigen sleutel in localStorage): alleen "Online only" en "Online
  // first"; platform en model volgen het filter van de pagina. Online wordt bepaald
  // uit de VIP-rijen zelf (zie vipOnlineLijst), zonder extra query.
  const [vipKeuze, zetVipKeuzeRuw] = useState<VolgerKeuze>(STANDAARD_KEUZE)
  useEffect(() => { if (mijnId != null) zetVipKeuzeRuw(leesKeuze(mijnId, 'vip')) }, [mijnId])
  const zetVipKeuze = (k: Partial<VolgerKeuze>) => {
    const nieuw = { ...vipKeuze, ...k }
    zetVipKeuzeRuw(nieuw)
    if (mijnId != null) bewaarKeuze(mijnId, nieuw, 'vip')
  }
  const vipfqs = filterQs(filter)
  const vipQ = useApi<Page<Convo>>(
    '/api/admin/conversations?limit=600&offset=0&skip_total=true&vip=true' +
    '&sort_order=desc&dashboard_blocked=false' + vipfqs,
    [vipfqs],
  )
  // 06-10-2026: een chatter krijgt alleen zijn toegewezen modellen
  // (`assigned_only`); zonder die vlag gaf de server alle modellen terug.
  const massSent = useApi<MassRow[] | { data: MassRow[] }>(
    '/api/admin/conversations/mass-message/history?limit=20' +
    (feed === 'chatter' ? '&assigned_only=true' : '') + fqs, [feed, fqs])

  // Zelfde ritme als de oude pagina; gemeten, niet geraden.
  usePoll(2500, grid.refetch)
  usePoll(2500, activity.refetch)
  usePoll(15000, vipQ.refetch)
  usePoll(5000, queue.refetch)
  usePoll(15000, volgersQ.refetch)
  usePoll(15000, volgersOnlineQ.refetch)
  // De modelkaarten en hun aantallen veranderen niet per seconde; elke vijf
  // seconden opnieuw ophalen kostte alleen maar servertijd.
  usePoll(60000, personas.refetch)
  usePoll(12000, priv.refetch)
  usePoll(15000, onlineQ.refetch)
  usePoll(15000, telQueue.refetch, !!telPad)

  /** Foto per model-slug, voor elke rij en elke kaart. */
  const avatarOf = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of personas.data ?? []) {
      if (p.slug && p.avatar_url) m.set(String(p.slug), p.avatar_url)
      for (const slug of p.persona_slugs ?? []) {
        if (slug && p.avatar_url) m.set(String(slug), p.avatar_url)
      }
    }
    return (slug: unknown) => m.get(String(slug ?? '')) ?? undefined
  }, [personas.data])

  /**
   * De tegelrij bovenaan: één kaart per SLUG, niet per persona. 02-10-2026
   * (Jordy): de berekening staat in lib/modelTegels.ts, zodat de Easy-stand
   * dezelfde modellen toont.
   */
  const buckets = useMemo(
    // 18-10-2026 (Jordy): owner/admin zien in My chats de claims van alle chatters.
    () => bucketPanels((queue.data?.data ?? []) as QueueRow[], null, isBeheer(mijnRol.data)),
    [queue.data, mijnRol.data],
  )
  // Echte en maybe-timewasters staan niet in het Online-paneel (zie panels.ts).
  const vipAlle = useMemo(() => vipLijst((vipQ.data?.data ?? []) as QueueRow[]), [vipQ.data])
  const vipRijen = useMemo(() => vipOnlineLijst(vipAlle, vipKeuze), [vipAlle, vipKeuze])
  const vipOnlineAantal = useMemo(() => aantalVersOnline(vipAlle), [vipAlle])
  // Het online-signaal is één voor het hele merk; het Online-blok vraagt het al op.
  const vipSignaal = onlineQ.data?.online_status_available ?? null
  const onlineRijen = useMemo(
    () => ((onlineQ.data?.data ?? []) as QueueRow[]).filter((c) => !isGemarkeerd(c)),
    [onlineQ.data],
  )
  const volgerOnline = useMemo(
    () => nieuweVolgers((volgersOnlineQ.data?.data ?? []) as QueueRow[], (queue.data?.data ?? []) as QueueRow[]),
    [volgersOnlineQ.data, queue.data],
  )
  const volgerAlle = useMemo(
    () => nieuweVolgers((volgersQ.data?.data ?? []) as QueueRow[], (queue.data?.data ?? []) as QueueRow[]),
    [volgersQ.data, queue.data],
  )
  const volgerRijen = useMemo(
    () => volgerLijst(volgerAlle, volgerOnline, volgerKeuze),
    [volgerAlle, volgerOnline, volgerKeuze],
  )
  const volgerOnlineAantal = useMemo(() => aantalVersOnline(volgerOnline), [volgerOnline])
  const volgerSignaal = volgersOnlineQ.data?.online_status_available ?? null
  // 2-11-2026 (Jordy, opdracht E): het kopje toont altijd het totaal, met het aantal
  // online ernaast; vroeger toonde het met "Online only" aan het gefilterde aantal
  // ("New followers (3)" terwijl er 744 waren). Het filter staat in een eigen melding.
  const volgerTotaal = kopTotaal(volgerAlle.length, volgersQ.data?.total)
  const volgerFilterTekst = actiefFilter(volgerKeuze)
  const vipFilterTekst = actiefFilter(vipKeuze)

  /**
   * 18-10-2026 (Jordy): het getal op een model-card is het aantal NU OPEN chats
   * (dezelfde rijen als de "open chats"-balk), niet meer alle gesprekken ooit.
   * Meeste open links, aflopend. Met een gekozen model komt de telling uit
   * `telQueue` (zelfde query zonder modelfilter), anders uit `queue` zelf.
   */
  const openRijen = useMemo(
    () => (telPad
      ? bucketPanels((telQueue.data?.data ?? []) as QueueRow[], null, isBeheer(mijnRol.data)).open
      : buckets.open),
    [telPad, telQueue.data, buckets.open, mijnRol.data],
  )
  const kiesTegels = useMemo(() => modelTegels(personas.data, platform), [personas.data, platform])
  // 23-10-2026 (Jordy): "N new" = open én sinds het laatste fanbericht nog niet bekeken.
  const tiles = useMemo(
    () => tegelsOpOpen(kiesTegels, openPerSlug(openRijen),
                       openPerSlug(openRijen.filter((c) => !alreadyOpened(c)))),
    [kiesTegels, openRijen],
  )
  const totalOpen = openRijen.length

  /** "02/09, 19:09" — het tijdstip in de kopbalk, zoals oud het schrijft. */
  const sinceStamp = (v: unknown): string => {
    if (!v) return ''
    const d = new Date(String(v))
    if (Number.isNaN(d.getTime())) return ''
    const p = (n: number) => String(n).padStart(2, '0')
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}, ${p(d.getHours())}:${p(d.getMinutes())}`
  }

  /** Kopbalk: "13 open chats (10 FC · 3 F2F) · oldest waiting 3d 16u — FC …". */
  const banner = useMemo(() => {
    const open = buckets.open
    const fc = open.filter((c) => String(c.platform).toLowerCase() === 'fancentro').length
    // De lijst staat op oudste-eerst, dus de langst wachtende chat is de
    // eerste. Toen de sortering nog aflopend was stond hier het einde.
    const oldest = open[0]
    const stamp = oldest ? (oldest.queue_fan_message_at || oldest.last_fan_message_at) : null
    return {
      n: open.length, fc, f2f: open.length - fc,
      oldest: oldest ? {
        stamp,
        wait: waited(stamp),
        platform: String(oldest.platform).toLowerCase() === 'fancentro' ? 'FC' : 'F2F',
        slug: String(oldest.model_slug ?? ''),
        // Oud schrijft hier "02/09, 19:09" — dag/maand met schuine strepen.
        since: sinceStamp(stamp),
      } : null,
    }
  }, [buckets])

  /** De kleur van de kopbalk: de zwaarste urgentie van alles wat openstaat. */
  const bannerUrgentie = useMemo(
    () => ergsteUrgentie(buckets.open.map(
      (c) => c.queue_fan_message_at || c.last_fan_message_at)),
    [buckets.open])

  const rows = useMemo(() => {
    // Model en platform filtert de server al (zie `fqs`).
    const gezien = new Set<number>()
    let list = [...(grid.data?.data ?? []), ...meer].filter((c) => !gezien.has(c.id) && (gezien.add(c.id), true))
    if (chatter !== 'all') list = list.filter((c) => c.claimed_by_name === chatter)
    if (alleenAi) list = list.filter((c) => c.ai_opted_in && !c.is_excluded_from_ai)
    // "Unread only" = het laatste fan-bericht is nieuwer dan wat het dashboard
    // gelezen heeft. Niet "onbeantwoord" (dat is de wachtrij).
    if (view === 'unread') {
      list = list.filter(
        (c) => !!c.last_fan_message_at &&
          (!c.last_read_at || c.last_fan_message_at > c.last_read_at),
      )
    }
    // Geen zoekfilter meer hier: de server heeft al gezocht (zie `zoekTraag`).
    // Opnieuw filteren op de geladen bladzijde zou een treffer wegstrepen
    // waarvan het woord in een ouder bericht staat in plaats van in de
    // voorvertoning.
    const out = [...list]
    // "Spend" komt al gesorteerd van de server (totaal over alle modellen); niet opnieuw.
    if (sort !== 'spend') {
      out.sort((a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    }
    return out
  }, [grid.data, meer, chatter, view, sort, alleenAi])

  /** Het getal achter "of": het aantal rijen dat na filteren overblijft. Bij
   *  een onaangeroerd filter is dat het servertotaal. */
  // Model en platform tellen niet mee: daar is het servertotaal al gefilterd.
  const filtered = chatter !== 'all' || view === 'unread' || !!search.trim()
  const shownTotal = filtered ? rows.length : (grid.data?.total ?? 0)
  // 21-10-2026 (Jordy): bulkacties in All chats, alleen owner/admin (de server weigert de rest).
  const magBulk = ['owner', 'admin'].includes(String(mijnRol.data?.role ?? '').toLowerCase())
  const zichtbaarGekozen = rows.filter((c) => selectie.has(Number(c.id)))
  const allesGekozen = rows.length > 0 && zichtbaarGekozen.length === rows.length
  const kiesRij = (id: number) => setSelectie((g) => {
    const n = new Set(g)
    if (n.has(id)) n.delete(id); else n.add(id)
    return n
  })
  const bulk = async (action: 'clear_timewaster' | 'clear_maybe_timewaster') => {
    if (!zichtbaarGekozen.length || bulkBezig) return
    setBulkBezig(true)
    await Promise.resolve(run({
      label: action === 'clear_timewaster' ? 'Remove timewaster' : 'Remove maybe timewaster',
      request: mut.post('/api/admin/conversations/panels/all/bulk-flags', {
        conversation_ids: zichtbaarGekozen.map((c) => Number(c.id)), action,
      }),
      onDone: () => { setSelectie(new Set()); setMeer([]); setMeerKlaar(false); grid.refetch(); queue.refetch() },
    })).finally(() => setBulkBezig(false))
  }
  const kolommen = magBulk
    ? COLS.map((k) => k.key !== 'sel' ? k : {
      ...k,
      label: (
        <span className={allesGekozen ? 'chk on' : 'chk'} role="checkbox" aria-checked={allesGekozen}
              aria-label="Select all loaded chats" title="Select all loaded chats" data-testid="allchats-alles"
              onClick={() => setSelectie(allesGekozen ? new Set() : new Set(rows.map((c) => Number(c.id))))}>
          {allesGekozen ? '✓' : ''}
        </span>
      ),
    })
    : COLS

  /** "2 unread total" naast de schakelaar, zoals oud. */
  const unreadTotal = useMemo(
    () => (grid.data?.data ?? []).filter(
      (c) => !!c.last_fan_message_at &&
        (!c.last_read_at || c.last_fan_message_at > c.last_read_at)).length,
    [grid.data],
  )

  // 21-10-2026 (Jordy): (maybe-)timewasters staan in geen enkel ander hoofdpaneel.
  const privRows = (priv.data?.conversations ?? []).filter((c) => !isGemarkeerd(c as QueueRow))

  const chatterNames = useMemo(
    () => (chatters.data ?? []).map((c) => c.name).filter(Boolean),
    [chatters.data],
  )
  /** Alle tegels, ongeacht het platformfilter: nodig om een slug bij zijn platform te vinden. */
  const alleTegels = useMemo(() => modelTegels(personas.data, 'all'), [personas.data])
  const lijstTegels = useMemo(() => modelTegels(personas.data, lijstPlatform), [personas.data, lijstPlatform])
  const gekozen = alleTegels.find((t) => t.slug === model)
  const modelLabel = gekozen
    ? `${gekozen.name} ${gekozen.platform === 'fancentro' ? 'FC' : 'F2F'}` : ''

  return (
    <>
      <div className={mobiel && !mobielFilters ? 'filters m-dicht' : 'filters'} data-tour="filters">
        {mobiel ? (
          <button type="button" className={mobielFilters ? 'btn sm on m-filterknop' : 'btn ghost sm m-filterknop'}
                  aria-expanded={mobielFilters} onClick={() => setMobielFilters(!mobielFilters)}>
            Filters {model || platform !== 'all' ? '•' : ''} {mobielFilters ? '▴' : '▾'}
          </button>
        ) : null}
        <Seg options={PLATFORMS} value={platform}
             onChange={(p) => zetFilter(platformKies(filter, p, alleTegels))} platform />
        <select className="select" aria-label="Model filter" value={model || 'all'}
                onChange={(e) => zetFilter(modelKies(filter, e.target.value, alleTegels))}>
          <option value="all">All models</option>
          {/* De keuzelijst houdt de vaste volgorde (springt niet mee met de open-telling). */}
          {kiesTegels.map((t) => (
            <option key={t.key} value={t.slug}>
              {t.name} {t.platform === 'fancentro' ? 'FC' : 'F2F'}
            </option>
          ))}
        </select>
        <select className="select" value={chatter} onChange={(e) => setChatter(e.target.value)}>
          <option value="all">All chatters</option>
          {chatterNames.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          className="btn ghost sm"
          type="button"
          onClick={() => window.open('/api/admin/posting/live-screenshot?which=f2f_chat', '_blank')}
        >
          F2F chat browser
        </button>
        {/* 30-09-2026 (Jordy): alleen wie Mass DM mag, ziet de knop. */}
        {magMassDm(mijnRol.data) ? (
          <button className="btn ghost sm" type="button" onClick={() => setMassOpen(true)}>
            Mass DM
          </button>
        ) : null}
        {/* 05-10-2026 (Jordy): je eigen geplande Mass DM's zien en annuleren. */}
        {magMassDm(mijnRol.data) ? (
          <button className="btn ghost sm" type="button" onClick={() => setEigenOpen(true)}>
            My scheduled
          </button>
        ) : null}
        {['owner', 'admin'].includes(String(mijnRol.data?.role ?? '').toLowerCase()) ? (
          <button className="btn ghost sm" type="button" data-testid="claims-losmaken"
                  onClick={() => setLosmakenOpen(true)}>
            Release chats
          </button>
        ) : null}
        {['owner', 'admin'].includes(String(mijnRol.data?.role ?? '').toLowerCase()) ? (
          <button className="btn ghost sm" type="button" data-testid="gesloten-chats"
                  onClick={() => setGeslotenOpen(true)}>
            Closed chats
          </button>
        ) : null}
        {/* 02-10-2026 (Jordy): Easy | Pro rechtsboven op de pagina. */}
        <span className="spacer" style={{ flex: 1 }} />
        {wissel}
      </div>

      {mobiel ? (
        <MobielInboxChips chips={[
          { paneel: 'My chats', label: 'My chats', aantal: buckets.mine.length },
          { paneel: 'Waiting queue', label: 'Waiting', aantal: buckets.waiting.length,
            urgent: bannerUrgentie === 'bad' },
          { paneel: 'Live Feed', label: 'Live' },
          { paneel: 'Spenders', label: 'Spenders', aantal: buckets.spenders.length },
          { paneel: 'Private chats', label: 'Private', aantal: privRows.length },
          { paneel: 'VIP', label: 'VIP', aantal: vipRijen.length },
          { paneel: 'Online', label: 'Online', aantal: onlineRijen.length },
          { paneel: 'Maybe timewasters', label: 'Maybe TW', aantal: buckets.timewasters.length },
          // 26-10-2026: New followers in plaats van Unlocks (dat blok is van de pagina af).
          { paneel: 'New followers', label: 'New followers',
            aantal: volgerTotaal },
          { paneel: 'All chats', label: 'All chats' },
        ]} />
      ) : null}

      {/* Modelkaarten zoals oud: verticaal, foto boven, platformbadge
          rechtsboven, dan naam / "N open" / "N new". 18-10-2026 (Jordy): meeste
          open chats links, aflopend; de "All"-kaart blijft ervoor. */}
      <div className="mgrid" data-tour="model-cards">
        <button
          type="button"
          className={!model ? 'mcard allcard on' : 'mcard allcard'}
          onClick={() => zetFilter({ model: '', platform })}
        >
          <span className="mname">All</span>
          <span className="mmeta">{totalOpen} open</span>
        </button>
        {tiles.map((t) => (
          <button
            type="button"
            className={model === t.slug ? 'mcard on' : 'mcard'}
            key={t.key}
            aria-pressed={model === t.slug}
            onClick={() => zetFilter(tegelKlik(filter, t))}
          >
            <span className="mplat"><Plat value={t.platform} /></span>
            <Avatar name={t.name} src={t.avatar} size={40} />
            <span className="mname">{t.name}</span>
            {/* 18-10-2026 (Jordy): open chats nu, niet alle gesprekken ooit. */}
            <span className="mmeta">{t.open} open</span>
            {t.unread ? <span className="mnew">{t.unread} new</span> : null}
          </button>
        ))}
      </div>

      <MassSentBar rows={listOf(massSent.data)} avatarOf={avatarOf} />

      {/* Brede balk met het aantal open chats — in oud een eigen rij tussen
          de mass-balk en de Live Feed, niet een chip in de filterrij. */}
      {/* De balk volgt dezelfde regel als de rijen: zodra er iemand langer
          dan een uur wacht, kleurt hij rood. */}
      <div className={`openbar u-${bannerUrgentie}`} data-tour="open-chats-bar">
        <b>{banner.n}</b>
        <span>open chats</span>
        <span className="mut2">({banner.fc} FC · {banner.f2f} F2F)</span>
        {banner.oldest ? (
          <>
            {/* Oud zet hier een dun scheidingslijntje, geen liggend streepje. */}
            <span className="vdiv" />
            <span className="mut2">oldest waiting</span>
            <Wacht sinds={banner.oldest.stamp} />
            <span className="mut2">
              — {banner.oldest.platform} {banner.oldest.slug} · since {banner.oldest.since}
            </span>
          </>
        ) : null}
      </div>

      {/* 18-10-2026 (Jordy): de 5-minuten-antwoordklok als band, alleen als er iets aftelt. */}
      <KlokTeller />

      {/* Raster exact zoals oud (gemeten bij 1920px): Live Feed vol breed,
          daarna vier rijen van twee panelen van gelijke breedte, elk 240px
          hoog met een eigen scrollbalk. */}
      <div className="grid full" style={{ marginBottom: 16 }}>
        <LiveFeedPanel
          rows={activity.rows}
          hasMore={activity.hasMore}
          loadingOlder={activity.loadingOlder}
          onLoadOlder={activity.loadOlder}
          loadingDays={activity.loadingDays}
          onLoadDays={() => activity.loadDays(FEED_KNOP_DAGEN)}
          chatters={chatterNames}
          chatter={feedChatter}
          onChatter={setFeedChatter}
          platform={platform}
          onPlatform={(p) => zetFilter(platformKies(filter, p, alleTegels))}
          avatarOf={avatarOf}
        />
      </div>

      <div className="grid two" style={{ marginBottom: 16 }}>
        {/* Op verzoek van Jordy omgedraaid: de wachtrij links, de spenders
            rechts. */}
        {/* 26-10-2026 (Jordy): vaste volgorde, boven naar onder en links naar rechts:
            1 Waiting · 2 Spenders · 3 My chats · 4 VIP · 5 New followers · 6 Private ·
            7 Online · 8 Maybe timewasters. Unlocks & Tips is van deze pagina af (elke
            chatter ziet zijn eigen unlocks en tips onder My Earnings). */}
        <WaitingPanel rows={buckets.waiting} avatarOf={avatarOf} />
        <SpendersPanel rows={buckets.spenders} avatarOf={avatarOf} />

        <MyChatsPanel
          rows={buckets.mine}
          allesZien={isBeheer(mijnRol.data)}
          onRelease={['owner', 'admin'].includes(String(mijnRol.data?.role ?? '').toLowerCase())
            ? (doel) => setLosmakenOpen(doel) : undefined}
          avatarOf={avatarOf}
          onClose={(id) => setWegklik(id)}
        />
        <VipPanel rows={vipRijen} avatarOf={avatarOf}
          total={kopTotaal(vipAlle.length)}
          online={vipSignaal === false ? null : vipOnlineAantal}
          filterBalk={vipFilterTekst
            ? <FilterBalk tekst={vipFilterTekst} testid="vip-filter-actief"
                          onReset={() => zetVipKeuze(STANDAARD_KEUZE)} />
            : null}
          leeg={vipKeuze.alleenOnline
            ? (vipSignaal === false
              ? 'Online status is not available right now (no live signal from the platforms yet).'
              : `No VIP fans online right now (online = seen online in the last ${ONLINE_VERS_MIN} minutes).`)
            : undefined}
          right={
            <span className="row" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button type="button" data-testid="vip-online"
                      className={vipKeuze.alleenOnline ? 'btn sm' : 'btn ghost sm'}
                      aria-pressed={vipKeuze.alleenOnline}
                      title={vipSignaal === false
                        ? 'Online status is not available right now (no live signal from the platforms yet)'
                        : `Only fans online in the last ${ONLINE_VERS_MIN} minutes`}
                      onClick={() => zetVipKeuze({ alleenOnline: !vipKeuze.alleenOnline })}>
                Online only ({vipSignaal === false ? '–' : vipOnlineAantal})
              </button>
              <button type="button" data-testid="vip-online-eerst"
                      className={vipKeuze.onlineEerst && !vipKeuze.alleenOnline ? 'btn sm' : 'btn ghost sm'}
                      aria-pressed={vipKeuze.onlineEerst}
                      disabled={vipKeuze.alleenOnline}
                      title={`Fans online in the last ${ONLINE_VERS_MIN} minutes on top`}
                      onClick={() => zetVipKeuze({ onlineEerst: !vipKeuze.onlineEerst })}>
                Online first
              </button>
            </span>
          }
        />

        <NewFollowersPanel
          rows={volgerRijen}
          total={volgerTotaal}
          online={volgerSignaal === false ? null : volgerOnlineAantal}
          filterBalk={volgerFilterTekst
            ? <FilterBalk tekst={volgerFilterTekst} testid="volgers-filter-actief"
                          onReset={() => zetVolgerKeuze(STANDAARD_KEUZE)} />
            : null}
          avatarOf={avatarOf}
          // Met "Online only" staat alles er al (de online-lijst wordt niet per 100 geladen).
          onMeer={!volgerKeuze.alleenOnline && !volgersQ.loading
            && (volgersQ.data?.data ?? []).length < Number(volgersQ.data?.total ?? 0)
            ? () => setVolgerLimiet((n) => n + 100) : undefined}
          leeg={volgerKeuze.alleenOnline
            ? (volgerSignaal === false
              ? 'Online status is not available right now (no live signal from the platforms yet).'
              : `No new followers online right now (online = seen online in the last ${ONLINE_VERS_MIN} minutes).`)
            : undefined}
          right={
            <span className="row" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button type="button" data-testid="volgers-online"
                      className={volgerKeuze.alleenOnline ? 'btn sm' : 'btn ghost sm'}
                      aria-pressed={volgerKeuze.alleenOnline}
                      title={volgerSignaal === false
                        ? 'Online status is not available right now (no live signal from the platforms yet)'
                        : `Only fans online in the last ${ONLINE_VERS_MIN} minutes`}
                      onClick={() => zetVolgerKeuze({ alleenOnline: !volgerKeuze.alleenOnline })}>
                Online only ({volgerSignaal === false ? '–' : volgerOnlineAantal})
              </button>
              <button type="button" data-testid="volgers-online-eerst"
                      className={volgerKeuze.onlineEerst && !volgerKeuze.alleenOnline ? 'btn sm' : 'btn ghost sm'}
                      aria-pressed={volgerKeuze.onlineEerst}
                      disabled={volgerKeuze.alleenOnline}
                      title={`Fans online in the last ${ONLINE_VERS_MIN} minutes on top`}
                      onClick={() => zetVolgerKeuze({ onlineEerst: !volgerKeuze.onlineEerst })}>
                Online first
              </button>
            </span>
          }
          onWeg={(id) => {
            if (volgerBezig.current.has(id)) return
            volgerBezig.current.add(id)
            void Promise.resolve(run({
              label: 'Remove new follower',
              request: mut.post(`/api/admin/conversations/${id}/dismiss-new-follower`),
              onDone: () => { volgersQ.refetch(); volgersOnlineQ.refetch() },
            })).finally(() => volgerBezig.current.delete(id))
          }}
        />
        <PrivatePanel
          rows={privRows}
          total={Math.max(privRows.length, (priv.data?.total_all ?? privRows.length)
            - ((priv.data?.conversations ?? []).length - privRows.length))}
          modelLabel={modelLabel}
          platform={platform}
          avatarOf={avatarOf}
          // 01-10-2026 (Jordy): was een plaatshouder (zette alleen de tabel
          // onderaan op "All"). Alleen wie privé-chats mag beheren, ziet de knop.
          onManageAll={magPriveBeheer(mijnRol.data) ? () => setPriveBeheerOpen(true) : undefined}
        />

        <OnlinePanel rows={onlineRijen} avatarOf={avatarOf}
                     beschikbaar={onlineQ.data?.online_status_available ?? null} />
        <TimewastersPanel rows={buckets.timewasters} avatarOf={avatarOf} />
      </div>

      <Panel
        title="All chats"
        flush
        right={
          <>
            {/* Welke gesprekken op AI staan. Het aantal rechts is het TOTAAL
                over alle gesprekken; de knop filtert wat er nu geladen is,
                want de lijst komt per pagina van de server. */}
            <button type="button"
                    className={alleenAi ? 'btn sm' : 'btn ghost sm'}
                    title="Show only chats with AI turned on"
                    onClick={() => setAlleenAi((v) => !v)}>
              AI on{aiStand.data ? ` · ${num(aiStand.data.conversations_ai_on)}` : ''}
            </button>
            <Seg options={VIEWS} value={view} onChange={setView} />
            <Seg options={SORTS} value={sort} onChange={setSort} />
            <button type="button" data-testid="allchats-online"
                    className={alleenOnline ? 'btn sm' : 'btn ghost sm'}
                    title="Only fans the platform reported online in the last 10 minutes"
                    onClick={() => setAlleenOnline((v) => !v)}>
              Online now
            </button>
            <button type="button" data-testid="allchats-timewasters"
                    className={twFilter === 'yes' ? 'btn sm' : 'btn ghost sm'}
                    title="Only fans marked as timewaster (they are not shown anywhere else on this page)"
                    onClick={() => setTwFilter((v) => (v === 'yes' ? '' : 'yes'))}>
              Timewasters
            </button>
            <button type="button" data-testid="allchats-maybe-timewasters"
                    className={twFilter === 'maybe' ? 'btn sm' : 'btn ghost sm'}
                    title="Only fans marked as maybe timewaster"
                    onClick={() => setTwFilter((v) => (v === 'maybe' ? '' : 'maybe'))}>
              Maybe timewasters
            </button>
            <Seg options={PLATFORMS} value={lijstPlatform} platform
                 onChange={(v) => { setLijstPlatform(v); setLijstModel('all') }} />
            <select className="select sm" aria-label="All chats model filter" data-testid="allchats-model"
                    value={lijstModel} onChange={(e) => setLijstModel(e.target.value)}>
              <option value="all">All models</option>
              {lijstTegels.map((t) => (
                <option key={t.key} value={t.slug}>{t.name} {t.platform === 'fancentro' ? 'FC' : 'F2F'}</option>
              ))}
            </select>
            <span className="mut2" style={{ fontSize: 12 }}>{unreadTotal} unread total</span>
          </>
        }
      >
        <div className="filters" style={{ margin: '12px 14px 6px' }}>
          <input
            className="inp"
            style={{ maxWidth: 320 }}
            placeholder="Search fans, models or message"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="spacer" style={{ flex: 1 }} />
          {/* 20-10-2026 (Jordy): geen bladzijden meer; scrollen laadt vanzelf bij. */}
          <span className="mut2" style={{ fontSize: 12 }} data-testid="allchats-telling">
            {rows.length} of {shownTotal}
          </span>
        </div>
        {magBulk && zichtbaarGekozen.length ? (
          <div className="row bulkbalk" data-testid="allchats-bulkbalk">
            <b>{zichtbaarGekozen.length} selected</b>
            <button type="button" className="btn sm" disabled={bulkBezig} data-testid="bulk-tw-weg"
                    onClick={() => { void bulk('clear_timewaster') }}>Remove timewaster</button>
            <button type="button" className="btn sm" disabled={bulkBezig} data-testid="bulk-maybe-weg"
                    onClick={() => { void bulk('clear_maybe_timewaster') }}>Remove maybe timewaster</button>
            <button type="button" className="btn ghost sm" onClick={() => setSelectie(new Set())}>Clear selection</button>
          </div>
        ) : null}
        {grid.loading && !grid.data ? <Loading /> : grid.error ? <ErrorNote error={grid.error} /> : (
          <Table cols={kolommen} emptyText={
            // 20-10-2026 (Jordy): zonder online-signaal eerlijk zeggen dat het onbekend is.
            alleenOnline && (grid.data as { online_status_available?: boolean } | null)?.online_status_available === false
              ? 'Online status is not available right now (no live signal from the platforms yet), so nobody can be shown as online.'
              : alleenOnline ? 'No fans online right now.' : 'No chats match this filter.'}>
            {rows.map((c) => (
              /* Klik op de rij opent het chatvenster, net als in oud. */
              <tr key={c.id} className="clickrow"
                  onClick={() => navigate(`/conversations/show/${c.id}`)}>
                <td onClick={(e) => e.stopPropagation()}>
                  {magBulk ? (
                    <span className={selectie.has(Number(c.id)) ? 'chk on' : 'chk'} role="checkbox"
                          aria-checked={selectie.has(Number(c.id))} aria-label={`Select chat ${c.id}`}
                          data-testid="allchats-kies" onClick={() => kiesRij(Number(c.id))}>
                      {selectie.has(Number(c.id)) ? '✓' : ''}
                    </span>
                  ) : <span className="chk" />}
                </td>
                <td className="mut2 mono">{c.id}</td>
                <td>
                  <b>{c.fan_display_name || c.fan_username || c.fan_id}</b>
                  {/* 20-10-2026 (Jordy): alleen als het platform zegt dat de fan nu online is. */}
                  {c.fan_online === true ? (
                    <span className="onlinelabel" data-testid="fan-online" title="Online now (platform status)">
                      <span className="onlinestip" aria-hidden="true" /> Online
                    </span>
                  ) : null}
                  {/* Een chatter moet de waarschuwing al in het overzicht
                      zien, niet pas als hij de chat opent. */}
                  {c.fan_custom_notes ? (
                    <span className="notitiestip" title={c.fan_custom_notes}>
                      ⚠
                    </span>
                  ) : null}
                  {c.fan_display_name && c.fan_username ? (
                    <div className="mut2" style={{ fontSize: 12 }}>{c.fan_username}</div>
                  ) : null}
                  {Number(c.fan_total_spend || 0) > 0 ? (
                    <div className="mut2" style={{ fontSize: 12 }}>
                      {eur(c.fan_total_spend)} spent
                    </div>
                  ) : null}
                  {/* 23-10-2026 (Jordy): nieuwe betaalde abonnee, nog niet persoonlijk bedankt. */}
                  <div><NieuweAbonneeTag c={c as unknown as QueueRow} /></div>
                </td>
                {/* 20-10-2026 (Jordy): totaal over alle modellen, het bedrag waarop "Spend" sorteert. */}
                <td className="num mono" title={`${eur(c.fan_total_spend)} with this model`}>{eur(alleModellen(c))}</td>
                <td>
                  <Plat value={c.platform} />
                  <a
                    className="tag mut"
                    style={{ marginLeft: 4, textDecoration: 'none' }}
                    href={`/api/admin/conversations/view/${c.id}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    ↗ {String(c.platform).toLowerCase() === 'fancentro' ? 'FC' : 'F2F'}
                  </a>
                </td>
                <td className="mut">
                  <Avatar
                    name={c.stage_name || c.model_slug}
                    src={c.avatar_url ?? avatarOf(c.model_slug)}
                  />
                  {c.stage_name || c.model_slug}
                </td>
                <td>
                  {/* 03-10-2026 (Jordy): alleen klikbaar voor wie AI mag schakelen. */}
                  <span
                    style={{ cursor: magAiSchakelen(mijnRol.data) ? 'pointer' : 'default' }}
                    title={magAiSchakelen(mijnRol.data) ? 'AI on/off for this chat' : undefined}
                    onClick={(e) => { e.stopPropagation(); if (!magAiSchakelen(mijnRol.data)) return; run({
                      label: 'AI toggle',
                      // `/ai-profile` bestaat niet; de route heet /toggle-ai
                      // en is een POST. Gaf 405 en dus een stille mislukking.
                      request: mut.post(`/api/admin/conversations/${c.id}/toggle-ai`,
                                        { is_excluded_from_ai: !c.is_excluded_from_ai }),
                      onDone: grid.refetch,
                      successText: c.is_excluded_from_ai ? 'AI on' : 'AI off',
                    }) }}
                  >
                    {c.ai_opted_in && !c.is_excluded_from_ai
                      ? <Tag kind="ok">On</Tag>
                      : <Tag kind="mut">Off</Tag>}
                  </span>
                </td>
                <td>{c.current_tag
                  ? <Tag kind={TAG_KIND[c.current_tag] || 'mut'}>{c.current_tag}</Tag>
                  : <span className="dim">—</span>}</td>
                <td className="num mono">{num(c.total_messages)}</td>
                <td className="num mono">{num(c.fan_messages)}</td>
                <td className="num mono">{num(c.ai_messages)}</td>
                <td className="mut2" style={{ fontSize: 12 }}>{dtFull(c.updated_at)}</td>
              </tr>
            ))}
          </Table>
        )}
        {grid.data ? (
          <div ref={eindRef} className="row" data-testid="allchats-einde"
               style={{ justifyContent: 'center', padding: '10px 0 14px' }}>
            {meerKlaar || (grid.data.data ?? []).length < size ? (
              <span className="mut2" style={{ fontSize: 12 }}>End of list.</span>
            ) : (
              <button className="btn ghost sm" type="button" disabled={meerBezig}
                      onClick={() => void laadMeer()}>{meerBezig ? 'Loading more…' : 'Load more'}</button>
            )}
          </div>
        ) : null}
      </Panel>

      {priveBeheerOpen ? (
        <PriveBeheerDialog
          totaal={priv.data?.total_all ?? privRows.length}
          onClose={() => setPriveBeheerOpen(false)}
          onKlaar={priv.refetch}
        />
      ) : null}

      {eigenOpen ? <MijnGeplandeMassDm onClose={() => setEigenOpen(false)} /> : null}
      {wegklik !== null ? <WegklikBevestig id={wegklik} onClose={() => setWegklik(null)} onDone={() => queue.refetch()} /> : null}
      {geslotenOpen ? <WeggekliktLijst onClose={() => setGeslotenOpen(false)} onDone={() => queue.refetch()} /> : null}
      {losmakenOpen !== false ? <ClaimsLosmaken start={losmakenOpen === true ? undefined : losmakenOpen}
                                               onClose={() => setLosmakenOpen(false)} onDone={() => queue.refetch()} /> : null}
      {massOpen ? (
        <MassDmSendDialog
          personas={personas.data ?? []}
          platformFilter={platform}
          onClose={() => setMassOpen(false)}
          magDirect={magMassDm(mijnRol.data, 'direct')}
          magNative={magMassDm(mijnRol.data, 'native')}
          magBetaald={magBetaaldeMassDm(mijnRol.data)}
        />
      ) : null}
    </>
  )
}
